/**
 * Admin Header Component
 *
 * Header for the admin section with user info and actions.
 *
 * @fileoverview Admin header with breadcrumbs and user menu
 */

import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { MdChevronRight, MdLogout, MdHome } from 'react-icons/md';
import { useAuth } from '../../hooks/useAuth';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useSupportMode } from '../../shared/support-mode';
import { AdminTenantSearch } from '../app/AdminTenantSearch';

/**
 * Generate breadcrumbs from current path
 */
function generateBreadcrumbs(pathname: string) {
  const parts = pathname.split('/').filter(Boolean);
  const breadcrumbs: { label: string; path: string }[] = [];

  let currentPath = '';
  for (const part of parts) {
    currentPath += `/${part}`;
    let label = part.charAt(0).toUpperCase() + part.slice(1);

    // Special cases
    if (part === 'admin') label = 'Admin';
    if (part === 'tenants') label = 'Tenants';
    if (part === 'new') label = 'Create';

    // UUID detection (skip as label, but keep path)
    if (part.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      label = 'Details';
    }

    breadcrumbs.push({ label, path: currentPath });
  }

  return breadcrumbs;
}

/**
 * Admin Header Component
 *
 * Displays breadcrumbs and user menu.
 */
export const AdminHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, user } = useAuth();
  const { profile } = useUserProfile();
  const { exitSupportMode } = useSupportMode();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');

  const breadcrumbs = generateBreadcrumbs(location.pathname);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleBackToApp = () => {
    // Clear support mode to avoid 4xx errors if the tenant was deleted
    exitSupportMode();
    navigate('/home');
  };

  return (
    <Box
      as="header"
      position="fixed"
      top={0}
      left="var(--xacto-sidebar-w)"
      right={0}
      h="16"
      bg={bgColor}
      borderBottom="1px"
      borderColor={borderColor}
      zIndex={10}
      transition="left 0.2s ease-in-out"
    >
      <Flex h="full" px={6} align="center" justify="space-between">
        {/* Breadcrumbs */}
        <Breadcrumb
          separator={<Icon as={MdChevronRight} color="gray.400" />}
          fontSize="sm"
        >
          {breadcrumbs.map((crumb, index) => (
            <BreadcrumbItem
              key={crumb.path}
              isCurrentPage={index === breadcrumbs.length - 1}
            >
              {index === breadcrumbs.length - 1 ? (
                <BreadcrumbLink as={Text} color={textColor} fontWeight="semibold">
                  {crumb.label}
                </BreadcrumbLink>
              ) : (
                <BreadcrumbLink as={Link} to={crumb.path} color="blue.500">
                  {crumb.label}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          ))}
        </Breadcrumb>

        {/* Right side: actions + user */}
        <Flex align="center" gap={4}>
          {/* Tenant search — enters support mode and navigates to app */}
          <AdminTenantSearch onTenantSelect={() => navigate('/home')} />

          {/* Back to App button */}
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Icon as={MdHome} />}
            onClick={handleBackToApp}
          >
            Back to App
          </Button>

          {/* User menu */}
          <Menu>
            <MenuButton>
              <Flex align="center" gap={2} cursor="pointer">
                <Avatar
                  size="sm"
                  name={profile?.displayName || user?.email || 'Admin'}
                  src={profile?.avatarUrl}
                />
                <Box textAlign="left" display={{ base: 'none', md: 'block' }}>
                  <Text fontSize="sm" fontWeight="medium" color={textColor}>
                    {profile?.displayName || 'Admin'}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {profile?.email || user?.email}
                  </Text>
                </Box>
              </Flex>
            </MenuButton>
            <MenuList>
              <MenuItem isDisabled>
                <Text fontSize="sm">{profile?.globalRole || 'ADMIN'}</Text>
              </MenuItem>
              <MenuDivider />
              <MenuItem onClick={handleSignOut} icon={<Icon as={MdLogout} />}>
                Sign Out
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Flex>
    </Box>
  );
};

export default AdminHeader;
